import React, { useState, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { styled } from '@mui/material/styles';

import { CreatePurchaseRequestDto, RequestCategory, RequestUrgency, RequestFrequency } from '../../types/request.types';
import { useAppSelector } from '../../store';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

interface PurchaseRequestFormProps {
  onSubmit: (values: CreatePurchaseRequestDto) => Promise<void>;
  initialValues?: Partial<CreatePurchaseRequestDto>;
  submitButtonText?: string;
  onBackToDashboard?: () => void;
}

const validationSchema = Yup.object({
  title: Yup.string().required('Title is required'),
  description: Yup.string().required('Description is required'),
  amount: Yup.number().min(0.01, 'Amount must be greater than 0').required('Amount is required'),
  currency: Yup.string().required('Currency is required'),
  category: Yup.string().required('Category is required'),
  urgency: Yup.string().required('Urgency is required'),
  frequency: Yup.string().required('Frequency is required'),
  justification: Yup.string().required('Business justification is required'),
  vendorName: Yup.string(),
  vendorLink: Yup.string().url('Must be a valid URL'),
  budgetCode: Yup.string(),
  requestedDeliveryDate: Yup.date().required('Requested delivery date is required'),
  // Optional payment fields with validation when provided
  paymentType: Yup.string().oneOf(['', 'personal_credit_card', 'company_credit_card', 'transfer', 'cash'], 'Invalid payment type'),
  paymentReference: Yup.string().when('paymentType', {
    is: (paymentType: string) => paymentType === 'personal_credit_card' || paymentType === 'company_credit_card',
    then: (schema) => schema.matches(/^\d{4}$/, 'Credit card reference must be exactly 4 digits'),
    otherwise: (schema) => schema
  }),
  attachments: Yup.array()
    .of(
      Yup.mixed<File>()
        .test('fileSize', 'File size must be less than 5MB', (value) => {
          if (!value) return true;
          return (value as File).size <= 5000000; // 5MB
        })
        .test('fileType', 'Only PDF, DOC, DOCX, XLS, XLSX files are allowed', (value) => {
          if (!value) return true;
          const allowedTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          ];
          return allowedTypes.includes((value as File).type);
        })
    )
    .max(5, 'Maximum 5 files allowed'),
});

const PurchaseRequestForm: React.FC<PurchaseRequestFormProps> = ({
  onSubmit,
  initialValues,
  submitButtonText = 'Submit Request',
  onBackToDashboard
}) => {
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const user = useAppSelector(state => state.auth.user);
  const navigate = useNavigate();

  // Payment reference helper functions
  const getPaymentReferenceHint = (paymentType: string) => {
    switch (paymentType) {
      case 'personal_credit_card':
      case 'company_credit_card':
        return 'Enter the last 4 digits of the credit card';
      case 'transfer':
        return 'Enter the reference number of the transfer';
      case 'cash':
        return 'Enter the reference number of the cash payment';
      default:
        return 'Enter payment reference';
    }
  };

  const getPaymentReferenceLabel = (paymentType: string) => {
    switch (paymentType) {
      case 'personal_credit_card':
      case 'company_credit_card':
        return 'Last 4 digits of credit card';
      case 'transfer':
        return 'Transfer reference number';
      case 'cash':
        return 'Cash reference number';
      default:
        return 'Payment reference';
    }
  };

  const handlePaymentReferenceChange = (value: string) => {
    if (formik.values.paymentType === 'personal_credit_card' || formik.values.paymentType === 'company_credit_card') {
      // For credit cards, only allow 4 digits
      const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
      formik.setFieldValue('paymentReference', digitsOnly);
    } else {
      // For transfer and cash, allow alphanumeric
      formik.setFieldValue('paymentReference', value);
    }
  };

  const handleBack = useCallback(() => {
    if (onBackToDashboard) {
      onBackToDashboard();
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [onBackToDashboard, navigate]);

  const formik = useFormik({
    initialValues: {
      title: initialValues?.title || '',
      description: initialValues?.description || '',
      amount: initialValues?.amount || 0,
      currency: 'USD',
      category: initialValues?.category || RequestCategory.OTHER,
      urgency: initialValues?.urgency || RequestUrgency.MEDIUM,
      frequency: initialValues?.frequency || RequestFrequency.ONCE,
      justification: initialValues?.justification || '',
      vendorName: initialValues?.vendorName || '',
      vendorLink: initialValues?.vendorLink || '',
      budgetCode: initialValues?.budgetCode || '',
      requestedDeliveryDate: initialValues?.requestedDeliveryDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week from now
      attachments: [],
      // Optional payment information
      paymentType: initialValues?.paymentType || '',
      paymentReference: initialValues?.paymentReference || '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        console.log('=== FORM SUBMISSION ===');
        console.log('Form values:', values);
        console.log('Attachments:', files);
        setError(null);
        await onSubmit({ ...values, attachments: files });
        console.log('Form submission successful');
      } catch (err) {
        console.error('Form submission error:', err);
        setError(err instanceof Error ? err.message : 'Failed to submit request');
      }
    },
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const newFiles = Array.from(event.target.files);
      setFiles(prevFiles => [...prevFiles, ...newFiles]);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={handleBack}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 1 }}>
              Purchase Request Form
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="title"
                name="title"
                label="Request Title"
                value={formik.values.title}
                onChange={formik.handleChange}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                id="description"
                name="description"
                label="Item Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                error={formik.touched.description && Boolean(formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="amount"
                name="amount"
                label="Estimated Cost"
                type="number"
                value={formik.values.amount}
                onChange={formik.handleChange}
                error={formik.touched.amount && Boolean(formik.errors.amount)}
                helperText={formik.touched.amount && formik.errors.amount}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="category-label">Category</InputLabel>
                <Select
                  labelId="category-label"
                  id="category"
                  name="category"
                  value={formik.values.category}
                  onChange={formik.handleChange}
                  error={formik.touched.category && Boolean(formik.errors.category)}
                  label="Category"
                >
                  {Object.values(RequestCategory).map((category) => (
                    <MenuItem key={category} value={category}>
                      {category.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.category && formik.errors.category && (
                  <FormHelperText error>{formik.errors.category}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                id="justification"
                name="justification"
                label="Business Justification"
                value={formik.values.justification}
                onChange={formik.handleChange}
                error={formik.touched.justification && Boolean(formik.errors.justification)}
                helperText={formik.touched.justification && formik.errors.justification}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="urgency-label">Urgency</InputLabel>
                <Select
                  labelId="urgency-label"
                  id="urgency"
                  name="urgency"
                  value={formik.values.urgency}
                  onChange={formik.handleChange}
                  error={formik.touched.urgency && Boolean(formik.errors.urgency)}
                  label="Urgency"
                >
                  {Object.values(RequestUrgency).map((urgency) => (
                    <MenuItem key={urgency} value={urgency}>
                      {urgency}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.urgency && formik.errors.urgency && (
                  <FormHelperText error>{formik.errors.urgency}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="frequency-label">Frequency</InputLabel>
                <Select
                  labelId="frequency-label"
                  id="frequency"
                  name="frequency"
                  value={formik.values.frequency}
                  onChange={formik.handleChange}
                  error={formik.touched.frequency && Boolean(formik.errors.frequency)}
                  label="Frequency"
                >
                  {Object.values(RequestFrequency).map((frequency) => (
                    <MenuItem key={frequency} value={frequency}>
                      {frequency.charAt(0) + frequency.slice(1).toLowerCase()}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.frequency && formik.errors.frequency && (
                  <FormHelperText error>{formik.errors.frequency}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Requested Delivery Date"
                value={formik.values.requestedDeliveryDate}
                onChange={(date) => formik.setFieldValue('requestedDeliveryDate', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: formik.touched.requestedDeliveryDate && Boolean(formik.errors.requestedDeliveryDate),
                    helperText: formik.touched.requestedDeliveryDate && formik.errors.requestedDeliveryDate as string,
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="vendorName"
                name="vendorName"
                label="Vendor Name (Optional)"
                value={formik.values.vendorName}
                onChange={formik.handleChange}
                error={formik.touched.vendorName && Boolean(formik.errors.vendorName)}
                helperText={formik.touched.vendorName && formik.errors.vendorName}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="vendorLink"
                name="vendorLink"
                label="Vendor Link (Optional)"
                value={formik.values.vendorLink}
                onChange={formik.handleChange}
                error={formik.touched.vendorLink && Boolean(formik.errors.vendorLink)}
                helperText={formik.touched.vendorLink && formik.errors.vendorLink}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="budgetCode"
                name="budgetCode"
                label="Budget Code (Optional)"
                value={formik.values.budgetCode}
                onChange={formik.handleChange}
                error={formik.touched.budgetCode && Boolean(formik.errors.budgetCode)}
                helperText={formik.touched.budgetCode && formik.errors.budgetCode}
              />
            </Grid>

            {/* Payment Information Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                Payment Information (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You can provide payment details now or fill them later during the approval process.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="payment-type-label">Payment Type</InputLabel>
                <Select
                  labelId="payment-type-label"
                  id="paymentType"
                  name="paymentType"
                  value={formik.values.paymentType}
                  label="Payment Type"
                  onChange={formik.handleChange}
                  error={formik.touched.paymentType && Boolean(formik.errors.paymentType)}
                >
                  <MenuItem value="">
                    <em>Select payment type (optional)</em>
                  </MenuItem>
                  <MenuItem value="personal_credit_card">Personal Credit Card</MenuItem>
                  <MenuItem value="company_credit_card">Company Credit Card</MenuItem>
                  <MenuItem value="transfer">Bank Transfer</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                </Select>
                {formik.touched.paymentType && formik.errors.paymentType && (
                  <FormHelperText error>{formik.errors.paymentType}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {formik.values.paymentType && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="paymentReference"
                  name="paymentReference"
                  label={getPaymentReferenceLabel(formik.values.paymentType)}
                  value={formik.values.paymentReference}
                  onChange={(e) => handlePaymentReferenceChange(e.target.value)}
                  error={formik.touched.paymentReference && Boolean(formik.errors.paymentReference)}
                  helperText={formik.touched.paymentReference && formik.errors.paymentReference || getPaymentReferenceHint(formik.values.paymentType)}
                  inputProps={{
                    maxLength: formik.values.paymentType === 'personal_credit_card' || formik.values.paymentType === 'company_credit_card' ? 4 : 50
                  }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
              >
                Upload Attachments
                <VisuallyHiddenInput
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  accept=".pdf,.png,.jpg,.jpeg"
                />
              </Button>
              {files.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {files.length} file(s) selected
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                >
                  {submitButtonText}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
  );
};

export default PurchaseRequestForm; 